import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Swal from 'sweetalert2';
import { useUser } from '../../context/UserContext';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

interface Asociado {
  id: string;
  asociado_id?: string;
  nombre: string;
  correo?: string;
  telefono?: string;
  calificacion?: number;
  inmobiliaria_doc_id?: string;
  años_experiencia?: number;
  estatus?: string;
  miembro_oficial?: boolean;
  propsActivas?: number;
  inmobiliaria?: string;
  [key: string]: any;
}

export default function AdminAsociados() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [asociados, setAsociados] = useState<Asociado[]>([]);
  const [filtro, setFiltro] = useState('');
  const [filtroInmobiliaria, setFiltroInmobiliaria] = useState('');
  const [loading, setLoading] = useState(true);
  const [paginaActual, setPaginaActual] = useState(1);
  const asociadosPorPagina = 15;

  useEffect(() => {
    if (!user) return;

    const fetchAsociados = async () => {
      if (!Array.isArray(user.tipo) || !user.tipo.includes('admin')) {
        Swal.fire({
          icon: 'error',
          title: 'Acceso denegado',
          text: 'No tienes permiso para ver esta sección.'
        });
        return navigate('/');
      }

      try {
        // Cargar asociados
        const asociadosSnap = await getDocs(collection(db, 'asociado'));

        const asociadosDocs = asociadosSnap.docs.filter(docSnap => {
          const data = docSnap.data();
          return !data.estatus || data.estatus === 'activo';
        });

        // Pre-cargar propiedades MLS para contar por inmobiliaria (evita N+1)
        const propsMlsSnap = await getDocs(
          query(collection(db, 'propiedades'), where('fuente', '==', 'flexmls'), where('estado', '==', 'A'))
        );
        const propsPorInmobiliaria = new Map<string, number>();
        propsMlsSnap.docs.forEach(d => {
          const inmId = d.data().inmobiliaria_doc_id;
          if (inmId) propsPorInmobiliaria.set(inmId, (propsPorInmobiliaria.get(inmId) || 0) + 1);
        });

        // Batch: pre-cargar inmobiliarias
        const inmobiliariaIds: string[] = [];
        asociadosDocs.forEach(d => {
          const inmId = d.data().inmobiliaria_doc_id;
          if (inmId && !inmobiliariaIds.includes(inmId)) inmobiliariaIds.push(inmId);
        });
        const inmobiliariaMap = new Map<string, string>();
        for (const inmId of inmobiliariaIds) {
          try {
            const inmoSnap = await getDoc(doc(db, 'inmobiliaria', inmId));
            if (inmoSnap.exists()) inmobiliariaMap.set(inmId, inmoSnap.data().nombre || 'Sin nombre');
          } catch {}
        }

        const lista: Asociado[] = asociadosDocs.map(docSnap => {
          const data = docSnap.data();
          const inmId = data.inmobiliaria_doc_id || '';
          return {
            id: docSnap.id,
            ...data,
            nombre: data.nombre || '',
            correo: data.correo,
            telefono: data.telefono,
            calificacion: data.calificacion,
            inmobiliaria_doc_id: inmId,
            años_experiencia: data.años_experiencia,
            estatus: data.estatus,
            propsActivas: inmId ? (propsPorInmobiliaria.get(inmId) || 0) : 0,
            inmobiliaria: inmobiliariaMap.get(inmId) || (inmId ? 'Sin nombre' : 'Sin inmobiliaria'),
          } as Asociado;
        });

        // ✅ Ordenar alfabéticamente por nombre
        lista.sort((a, b) => {
          const nombreA = (a.nombre || '').toLowerCase();
          const nombreB = (b.nombre || '').toLowerCase();
          return nombreA.localeCompare(nombreB);
        });

        setAsociados(lista);
      } catch (err) {
        console.error('Error al cargar asociados:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar la lista de asociados.'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAsociados();
  }, [user, navigate]);

  // ✅ Filtros aplicados
  // Sprint 10 (F4-008): si el filtro está vacío, devolver todos. Antes podía
  // devolver false cuando ambos campos eran undefined porque optional chaining
  // colapsaba a undefined en lugar de boolean.
  const asociadosFiltrados = asociados.filter(a => {
    const filtroLower = filtro.toLowerCase().trim();
    const matchTexto = filtroLower === '' || (
      (a.nombre?.toLowerCase().includes(filtroLower) ?? false) ||
      (a.correo?.toLowerCase().includes(filtroLower) ?? false)
    );
    const matchInmobiliaria = filtroInmobiliaria === '' ||
      (a.inmobiliaria?.toLowerCase().includes(filtroInmobiliaria.toLowerCase()) ?? false);

    return matchTexto && matchInmobiliaria;
  });

  // ✅ Paginación
  const totalPaginas = Math.ceil(asociadosFiltrados.length / asociadosPorPagina);
  const startIndex = (paginaActual - 1) * asociadosPorPagina;
  const asociadosPaginados = asociadosFiltrados.slice(startIndex, startIndex + asociadosPorPagina);

  // ✅ Obtener inmobiliarias únicas para filtro
  const inmobiliariasUnicas = Array.from(new Set(
    asociados.map(a => a.inmobiliaria).filter(Boolean)
  )).sort();

  const handleEliminar = async (id: string, nombre: string) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar asociado?',
      html: `¿Estás seguro de eliminar a <strong>${nombre}</strong>?<br>Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'asociado', id));
        setAsociados(prev => prev.filter(a => a.id !== id));

        Swal.fire({
          icon: 'success',
          title: 'Eliminado',
          text: `El asociado ${nombre} fue eliminado exitosamente.`
        });
      } catch (error) {
        console.error('Error eliminando asociado:', error);
        Swal.fire('Error', 'No se pudo eliminar el asociado. Intenta nuevamente.', 'error');
      }
    }
  };

  if (!user) {
    return (
      <div className="container mt-5">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-3">Cargando sesión...</p>
        </div>
      </div>
    );
  }

  if (!user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger text-center">
          <h4>Acceso denegado</h4>
          <p>No tienes permiso para ver esta sección.</p>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/admin-dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserNav />
      <div className="container-fluid px-4 mt-4 mb-5">
        <Breadcrumb items={[
          { label: 'Dashboard', path: '/admin-dashboard' },
          { label: 'Asociados' }
        ]} />

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mb-0">
            <i className="fas fa-user-friends me-2 text-primary"></i>Asociados
          </h2>
        </div>

        {/* Stats compactos */}
        <div className="d-flex gap-2 mb-3 flex-wrap">
          <span className="badge bg-primary fs-6 p-2">{asociados.length} Total</span>
          <span className="badge bg-success fs-6 p-2">
            {asociados.filter(a => a.propsActivas && a.propsActivas > 0).length} Con propiedades
          </span>
          <span className="badge bg-secondary fs-6 p-2">
            <i className="fas fa-filter me-1"></i>{asociadosFiltrados.length} mostrados
          </span>
        </div>

        {/* Filtros */}
        <div className="card mb-3">
          <div className="card-body py-2">
            <div className="row g-2">
              <div className="col-md-5">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Buscar nombre o correo..."
                  value={filtro}
                  onChange={e => { setFiltro(e.target.value); setPaginaActual(1); }}
                />
              </div>
              <div className="col-md-5">
                <select className="form-select form-select-sm" value={filtroInmobiliaria} onChange={e => { setFiltroInmobiliaria(e.target.value); setPaginaActual(1); }}>
                  <option value="">Todas las agencias</option>
                  {inmobiliariasUnicas.map(inmo => (
                    <option key={inmo} value={inmo}>{inmo}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-1">
                <button className="btn btn-outline-secondary btn-sm w-100" onClick={() => { setFiltro(''); setFiltroInmobiliaria(''); }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /><p className="mt-3">Cargando asociados...</p></div>
        ) : (
          <>
            <div className="table-responsive">
              <table aria-label="Tabla de Admin Asociado" className="table table-hover table-sm align-middle">
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: '25%' }}>Asociado</th>
                    <th scope="col">Correo</th>
                    <th scope="col">Agencia</th>
                    <th scope="col">Props MLS</th>
                    <th style={{ width: '100px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {asociadosPaginados.length > 0 ? (
                    asociadosPaginados.map(aso => (
                      <tr key={aso.id}>
                        <td>
                          <div className="fw-semibold">{aso.nombre}</div>
                          <div className="d-flex gap-1 mt-1 flex-wrap">
                            {aso.miembro_oficial && (
                              <span className="badge bg-success" style={{ fontSize: '0.65rem' }}>
                                <i className="fas fa-check-circle me-1"></i>Oficial
                              </span>
                            )}
                            {!aso.miembro_oficial && (
                              <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>
                                No oficial
                              </span>
                            )}
                          </div>
                          {(aso.años_experiencia || 0) > 0 && (
                            <small className="text-muted">{aso.años_experiencia} anos exp.</small>
                          )}
                        </td>
                        <td><small>{aso.correo || <span className="text-muted">-</span>}</small></td>
                        <td><small>{aso.inmobiliaria || 'Sin asignar'}</small></td>
                        <td>
                          <span className={`badge ${aso.propsActivas && aso.propsActivas > 0 ? 'bg-success' : 'bg-secondary'}`}>
                            {aso.propsActivas || 0}
                          </span>
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button className="btn btn-primary" onClick={() => navigate(`/ver-admin-asociado/${aso.id}`)} title="Ver">
                              <i className="fas fa-eye"></i>
                            </button>
                            <button className="btn btn-outline-warning" onClick={() => navigate(`/editar-asociado/${aso.id}`)} title="Editar">
                              <i className="fas fa-edit"></i>
                            </button>
                            <button className="btn btn-outline-danger" onClick={() => handleEliminar(aso.id, aso.nombre)} title="Eliminar">
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        No hay asociados que coincidan con los filtros.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginacion */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="text-muted">
                {asociadosFiltrados.length > 0 ? `${startIndex + 1}-${Math.min(startIndex + asociadosPorPagina, asociadosFiltrados.length)} de ${asociadosFiltrados.length}` : '0 resultados'}
              </small>
              {totalPaginas > 1 && (
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPaginaActual(paginaActual - 1)} disabled={paginaActual === 1}>
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    </li>
                    {Array.from({ length: Math.min(7, totalPaginas) }, (_, i) => {
                      let pageNum: number;
                      if (totalPaginas <= 7) pageNum = i + 1;
                      else if (paginaActual <= 4) pageNum = i + 1;
                      else if (paginaActual >= totalPaginas - 3) pageNum = totalPaginas - 6 + i;
                      else pageNum = paginaActual - 3 + i;
                      return (
                        <li key={pageNum} className={`page-item ${paginaActual === pageNum ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setPaginaActual(pageNum)}>{pageNum}</button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPaginaActual(paginaActual + 1)} disabled={paginaActual === totalPaginas}>
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </>
        )}
      </div>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
